/**
 * Text corruption utility for the Discovery UI system.
 * Uses Unicode combining characters to create glitchy text appearance.
 *
 * Three levels:
 * - heavy: Full corruption (every character gets combining chars)
 * - light: Partial corruption (~15% of characters)
 * - none: Passthrough (clean text)
 */

// Unicode combining characters for glitch effects
const COMBINING_CHARS = ['\u0337', '\u0338', '\u0335', '\u0336', '\u0334'];
const COMBINING_CHARS_LIGHT = ['\u0337', '\u0336'];

/**
 * Heavy corruption — every character gets a combining character.
 * Used for Phase 1 (Unknown) zone names and descriptions.
 * @param {string} text
 * @returns {string}
 */
export function heavy(text) {
    return text.split('').map(c => {
        if (c === ' ') return c;
        return c + COMBINING_CHARS[Math.floor(Math.random() * COMBINING_CHARS.length)];
    }).join('');
}

/**
 * Light corruption — ~15% of characters get a combining character.
 * Used for Phase 2 (Discovered) zone names and descriptions.
 * @param {string} text
 * @returns {string}
 */
export function light(text) {
    return text.split('').map(c => {
        if (c === ' ') return c;
        if (Math.random() < 0.15) {
            return c + COMBINING_CHARS_LIGHT[Math.floor(Math.random() * COMBINING_CHARS_LIGHT.length)];
        }
        return c;
    }).join('');
}

/**
 * No corruption — passthrough.
 * @param {string} text
 * @returns {string}
 */
export function none(text) {
    return text;
}

/**
 * Determine zone corruption phase based on Pyre level.
 * Phases (from lowest to highest pyre level):
 * - 'hidden': Below revealLevel (pyreLevel - 25). Tab/zone not visible at all.
 * - 'unknown': Between revealLevel and discoveryLevel. Heavy corruption, no names.
 * - 'discovered': Between discoveryLevel and pyreLevel. Light corruption, names visible.
 * - 'unlocked': At or above pyreLevel. Fully accessible.
 * - 'none': Zone is not pyre-gated (regular zone).
 * @param {Object} zone - Zone object with pyreVeilHidden and pyreLevel
 * @param {number} pyreLevel - Player's current Pyre level
 * @returns {'none'|'hidden'|'unknown'|'discovered'|'unlocked'}
 */
export function getZoneCorruptionPhase(zone, pyreLevel) {
    if (!zone.pyreVeilHidden) return 'none';
    const revealLevel = zone.pyreLevel - 25;
    const discoveryLevel = zone.pyreLevel - 10;
    if (pyreLevel >= zone.pyreLevel) return 'unlocked';
    if (pyreLevel >= discoveryLevel) return 'discovered';
    if (pyreLevel >= revealLevel) return 'unknown';
    return 'hidden';
}
